import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import CartItemsInCart from "../canvas/CartItemsInCart";
import cartAPI from "../../api/cartApi";
import Breadcrump from "../../components/Breadcrump";
import { showError, showSuccess } from "../../utils/toastUtils";
import Summary from "./common/Summary";
import { handleApiValidationError } from "../../utils/apiErrorHandler";

function Cart() {
  const { cartItems, fetchCart, cartSummary } = useCart();

  const [couponList, setCouponList] = useState([]);
  const [couponCode, setCouponCode] = useState(cartSummary?.couponCode || "");
  const [activeCoupon, setActiveCoupon] = useState(
    cartSummary?.couponCode || "",
  );

  useEffect(() => {
    fetchCouponCodes();
  }, []);

  useEffect(() => {
    setCouponCode(cartSummary?.couponCode || "");
    setActiveCoupon(cartSummary?.couponCode || "");
  }, [cartSummary]);

  const fetchCouponCodes = async () => {
    try {
      const res = await cartAPI.showCoupon();
      if (res.code === 200) {
        setCouponList(res.data);
      }
    } catch (err) {}
  };

  const applyCoupon = async (codeParam) => {
    const codeToApply = codeParam || couponCode;
    setActiveCoupon(codeToApply);
    setCouponCode(codeToApply);

    try {
      const res = await cartAPI.applyCoupon({
        couponCode: codeToApply,
      });

      if (res.code === 200) {
        await fetchCart();
        showSuccess("Coupon applied successfully");
      } else {
        setActiveCoupon("");
        showError(res.message || "Invalid coupon code");
      }
    } catch (err) {
      setActiveCoupon("");
      handleApiValidationError(err, showError);
    }
  };

  const removeCoupon = async () => {
    try {
      const res = await cartAPI.removeCoupon();
      if (res.code === 200) {
        setActiveCoupon("");
        setCouponCode("");
        fetchCart();
        showSuccess("Coupon removed");
      }
    } catch (err) {}
  };

  return (
    <div className="bg-[#f8f5f1] min-h-screen text-[#181512] font-['Inter',sans-serif] pb-20">
      <Breadcrump title="Cart" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        {cartItems && cartItems.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column: Cart Table & Coupons */}
            <div className="lg:col-span-8 space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-[0_4px_10px_rgba(0,0,0,0.04)] border border-[#eee7df]">
                {/* Desktop Table View */}
                <div className="overflow-x-auto hidden md:block">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-[#eee7df] text-xs uppercase tracking-wider text-[#9a9188] font-['Poppins',sans-serif]">
                        <th className="pb-4 font-semibold">Product</th>
                        <th className="pb-4 text-center font-semibold">MRP</th>
                        <th className="pb-4 text-center font-semibold">Price</th>
                        <th className="pb-4 text-center font-semibold">Quantity</th>
                        <th className="pb-4 text-center font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#eee7df]">
                      {cartItems.map((item) => (
                        <CartItemsInCart key={item._id} item={item} />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card Stack View */}
                <div className="md:hidden divide-y divide-[#eee7df]">
                  {cartItems.map((item) => (
                    <CartItemsInCart key={item._id} item={item} />
                  ))}
                </div>

                {/* Coupon Input Box */}
               <div className="mt-8 pt-6 border-t border-[#eee7df]">
  <label className="block text-sm font-medium font-['Poppins',sans-serif] text-[#5e564f] mb-2">
    Have a Spice Voucher?
  </label>

  {/* Responsive Input + Button */}
  <div className="flex flex-col sm:flex-row gap-3">
    <input
      className="flex-1 w-full bg-[#fbf9f6] border border-[#eee7df] rounded-xl px-4 py-3 text-sm text-[#181512] focus:outline-none focus:border-[#a63c06] transition-colors"
      type="text"
      placeholder="Enter discount code"
      value={couponCode}
      onChange={(e) => setCouponCode(e.target.value)}
    />
    <button
      type="button"
      className="w-full sm:w-auto px-6 py-3 rounded-xl font-medium text-sm transition-all duration-300 border border-[#a63c06] text-[#a63c06] hover:bg-[#a63c06] hover:text-white whitespace-nowrap"
      onClick={activeCoupon ? removeCoupon : () => applyCoupon()}
    >
      {activeCoupon ? "Remove" : "Apply"}
    </button>
  </div>

  {/* Available Coupon List */}
  {couponList && couponList.length > 0 && (
    <div className="mt-6 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#b08d57]">
        Available Luxury Offers
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {couponList.map((items) => (
          <div
            key={items._id}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-[#fbf9f6] border border-[#eee7df] hover:border-[#b08d57] transition-all"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="shrink-0 bg-[#f5c3a5]/30 text-[#a63c06] px-3 py-2 rounded-lg font-bold text-xs">
                {items?.discountType === "PERCENTAGE"
                  ? `${items?.discountValue}%`
                  : `₹${items?.discountValue}`}
              </div>

              <div className="min-w-0">
                <h6 className="text-sm font-semibold text-[#181512] break-words">
                  {items?.title}
                </h6>

                <p className="text-xs text-[#9a9188] break-all">
                  Code:{" "}
                  <span className="font-mono text-[#a63c06]">
                    {items?.code}
                  </span>
                </p>
              </div>
            </div>

            <button
              type="button"
              className={`w-full sm:w-auto text-xs px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeCoupon === items.code
                  ? "bg-[#4e6b4a] text-white"
                  : "bg-[#e8d8c3]/40 text-[#181512] hover:bg-[#a63c06] hover:text-white"
              }`}
              onClick={() => applyCoupon(items.code)}
            >
              {activeCoupon === items.code ? "Applied" : "Apply"}
            </button>
          </div>
        ))}
      </div>
    </div>
  )}
</div>
              </div>
            </div>

            {/* Right Column: Order Summary Sidebar */}
            <div className="lg:col-span-4">
              <div className="bg-white rounded-2xl p-6 shadow-[0_4px_10px_rgba(0,0,0,0.04)] border border-[#eee7df] sticky top-6">
                <div className="text-lg font-medium font-['Cormorant_Garamond',serif] text-[#181512] text-xl border-b border-[#eee7df] pb-4">
                  Order Details
                </div>

                <div className="py-4">
                  <Summary cartItems={cartItems} cartSummary={cartSummary} />
                </div>

                <div className="mt-6">
                  <Link
                    className=" custom-cart-btn w-full block text-center py-3.5 px-6 rounded-xl font-medium text-white bg-[#a63c06] hover:bg-[#7a2c04] transition-all shadow-md"
                    to="/checkout"
                  >
                    Proceed to Checkout
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 text-center shadow-[0_4px_10px_rgba(0,0,0,0.04)] border border-[#eee7df] max-w-2xl mx-auto my-12">
            <img
              src={`${process.env.PUBLIC_URL}/images/section/account-no-order.png`}
              alt="No Order Found"
              className="w-48 mx-auto mb-6 opacity-80"
            />
            <div className="text-2xl font-['Cormorant_Garamond',serif] font-medium text-[#181512] mb-6">
              Your Spice Chest is Empty
            </div>
            <Link
              to="/shop"
              className="inline-flex justify-center items-center px-8 py-3.5 rounded-xl text-white bg-[#181512] hover:bg-[#a63c06] transition-all font-medium text-sm"
            >
              Explore Collection
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default Cart;
